import { Article, ArticleProps } from '@/components/ui/Article';
import { Categories, CategoryCardInput } from '@/components/ui/Categories';
import { Header } from '@/components/ui/Header';
import { Languages, useLanguage, withLocale } from '@/contexts/LanguageContext';
import { Compact } from '@/utils';
import compact from 'lodash/compact';
import { GetServerSideProps } from 'next';
import ErrorPage from 'next/error';
import Head from 'next/head';
import { GetAllDataQuery, JobDetailsFragment } from 'queries/getAllData';
import React from 'react';
import { createSeoSnippet } from 'utils/createSeoSnippet';
import { getLookup } from 'utils/lookupHelpers';

type JobProps = ArticleProps<JobDetailsFragment> & {
  articles: Compact<GetAllDataQuery['allOverviewJobArticles']>;
  lang: Languages;
};

function JobPage(props: JobProps) {
  const { lang, articles, ...articleProps } = props;

  const { t } = useLanguage();

  if (!articles.length) {
    return (
      <>
        <Head>
          <meta name="robots" content="noindex" />
        </Head>
        <ErrorPage statusCode={404} />
      </>
    );
  }

  const categories: Array<CategoryCardInput> = articles.map((article) => {
    return {
      img: article.card_illustration?.media?.url ?? '',
      text: article?.[lang]?.title || '',
      url: `[lang]/aktuelle-jobs/${article.slug}`,
      scrollToTop: false,
    };
  });

  const currentSelected = articles.findIndex((article) => article.id === articleProps.article.id);

  return (
    <>
      <Header showLogoOnDesktop={true} />
      <div className="relative flex flex-col lg:max-w-xl md:items-start mt-16">
        <h1 className="mb-6 text-xl md:mb-10 md:text-3xl md:leading-tight">
          <strong className="font-bold">{t.general.jobs}</strong>
          <br />
          <span className="md:text-2xl">{t.general.chooseTopic}</span>
        </h1>
      </div>
      <Categories
        categories={categories}
        className=""
        inactiveCardClassName="opacity-50"
        activeCardClassName="opacity-100"
        defaultActive={currentSelected}
      />
      <Article className="mt-16" lang={lang} {...articleProps} />
    </>
  );
}

export default withLocale.hoc(JobPage);

export const getServerSideProps: GetServerSideProps<JobProps, { lang: Languages; slug: string }> = withLocale.getServerSideProps<
  JobProps,
  { slug: string }
>(async (strapiLookup, lang, params) => {
  const { slug } = params;
  const jobArticles = compact(strapiLookup.allOverviewJobArticles);
  const jobArticle = compact(strapiLookup.allFullJobArticles).find((job) => job.slug === slug);
  const learningModeData = strapiLookup.learningMode?.Learning_mode_keyword;
  if (!jobArticle) {
    throw new Error(`No job article with slug "${slug}" found`);
  }

  return {
    articles: jobArticles,
    originalLanguage: jobArticle[lang] ? null : lang,
    article: jobArticle,
    type: 'targetPage',
    title: jobArticle[lang]?.title,
    seoSnippet: createSeoSnippet(jobArticle[lang]?.text),
    keywords: jobArticle[lang]?.tags?.map((tag) => tag?.title).join(', '),
    learningModeData,
  };
}, getLookup);
